import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import { useEffect, useRef } from "react";
import {
  Bold, Italic, List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Table as TableIcon,
  Heading1, Heading2, Undo, Redo, Plus, Minus, Trash2, Quote,
} from "lucide-react";

const ToolbarButton = ({ onClick, active, disabled, title, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`p-1.5 rounded hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "bg-blue-100 text-blue-700" : "text-slate-600"}`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-slate-300 mx-0.5" />;

/**
 * Rich text editor based on TipTap. Supports bold/italic, headings, lists,
 * alignment, color, links, pasted/uploaded images, and tables.
 *
 * Props:
 *  - value: string (HTML) — initial content
 *  - onChange: (html: string) => void
 *  - placeholder: string
 *  - minHeight: number (px) — defaults to 220
 */
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 220 }) {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-blue-600 underline" } }),
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-3 py-2",
        style: `min-height:${minHeight}px`,
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items || [];
        for (const item of items) {
          if (item.type?.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (ev) => {
                view.dispatch(
                  view.state.tr.replaceSelectionWith(
                    view.state.schema.nodes.image.create({ src: ev.target.result })
                  )
                );
              };
              reader.readAsDataURL(file);
              event.preventDefault();
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Keep editor synced when value changes externally (form reset)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => editor.chain().focus().setImage({ src: ev.target.result }).run();
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const inTable = editor.isActive("table");

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita (Ctrl+B)" testId="rt-bold"><Bold className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva (Ctrl+I)" testId="rt-italic"><Italic className="w-3.5 h-3.5" /></ToolbarButton>
        <Sep />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Título 1" testId="rt-h1"><Heading1 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Título 2" testId="rt-h2"><Heading2 className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Cita" testId="rt-quote"><Quote className="w-3.5 h-3.5" /></ToolbarButton>
        <Sep />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista" testId="rt-ul"><List className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada" testId="rt-ol"><ListOrdered className="w-3.5 h-3.5" /></ToolbarButton>
        <Sep />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Alinear izquierda" testId="rt-left"><AlignLeft className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centrar" testId="rt-center"><AlignCenter className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Alinear derecha" testId="rt-right"><AlignRight className="w-3.5 h-3.5" /></ToolbarButton>
        <Sep />
        <ToolbarButton onClick={setLink} active={editor.isActive("link")} title="Insertar enlace" testId="rt-link"><LinkIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Insertar imagen" testId="rt-image"><ImageIcon className="w-3.5 h-3.5" /></ToolbarButton>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
        <Sep />
        <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insertar tabla 3×3" testId="rt-table"><TableIcon className="w-3.5 h-3.5" /></ToolbarButton>
        {inTable && (
          <>
            <ToolbarButton onClick={() => editor.chain().focus().addColumnAfter().run()} title="Agregar columna" testId="rt-col-add"><Plus className="w-3 h-3" />C</ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().deleteColumn().run()} title="Quitar columna" testId="rt-col-del"><Minus className="w-3 h-3" />C</ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().addRowAfter().run()} title="Agregar fila" testId="rt-row-add"><Plus className="w-3 h-3" />F</ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().deleteRow().run()} title="Quitar fila" testId="rt-row-del"><Minus className="w-3 h-3" />F</ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().deleteTable().run()} title="Eliminar tabla" testId="rt-table-del"><Trash2 className="w-3.5 h-3.5" /></ToolbarButton>
          </>
        )}
        <Sep />
        <input
          type="color"
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          title="Color de texto"
          className="w-6 h-6 cursor-pointer rounded"
          data-testid="rt-color"
        />
        <Sep />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer" testId="rt-undo"><Undo className="w-3.5 h-3.5" /></ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer" testId="rt-redo"><Redo className="w-3.5 h-3.5" /></ToolbarButton>
      </div>
      <div className="rt-editor-content">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
      <style>{`
        .rt-editor-content table { border-collapse: collapse; width: 100%; margin: 8px 0; }
        .rt-editor-content table td, .rt-editor-content table th { border: 1px solid #cbd5e1; padding: 6px 10px; min-width: 60px; vertical-align: top; }
        .rt-editor-content table th { background: #f1f5f9; font-weight: 600; }
        .rt-editor-content table .selectedCell { background: #dbeafe; }
        .rt-editor-content img { max-width: 100%; height: auto; border-radius: 4px; }
        .rt-editor-content blockquote { border-left: 3px solid #cbd5e1; padding-left: 12px; color: #475569; margin: 8px 0; }
        .rt-editor-content ul { list-style: disc; padding-left: 1.4em; }
        .rt-editor-content ol { list-style: decimal; padding-left: 1.4em; }
        .rt-editor-content h1 { font-size: 1.5em; font-weight: 700; margin: 0.4em 0 0.2em; }
        .rt-editor-content h2 { font-size: 1.25em; font-weight: 700; margin: 0.4em 0 0.2em; }
        .rt-editor-content p { margin: 0.3em 0; }
      `}</style>
    </div>
  );
}
